import {
	AppointmentType,
	AvailabilityByLocationDocument,
	AvailabilityByLocationQuery,
	AvailableSlot,
	BffBrandType,
} from '@src/lib/__generated/beffe/sdk';
import { getBeffeGraphQlClient } from '@src/lib/client';
import { IsPayloadBody } from '@src/utils/build-intelligent-scheduling-payload';

export interface FetchAvailableSlotsParams {
	beffeUrl: string;
	facilityId: string;
	appointmentTypes: AppointmentType[];
	dateFrom: string;
	dateTo: string;
	intelligentSchedulingEnabled?: boolean;
	intelligentSchedulingPayload?: IsPayloadBody | null;
}

/**
 * Fetches available appointment slots from BEFFE for a given facility and date range,
 * filtering out overflow provider slots (DCP-13324).
 */
export async function fetchAvailableSlots({
	beffeUrl,
	facilityId,
	appointmentTypes,
	dateFrom,
	dateTo,
	intelligentSchedulingEnabled = false,
	intelligentSchedulingPayload = null,
}: FetchAvailableSlotsParams): Promise<AvailableSlot[]> {
	const result = await getBeffeGraphQlClient(beffeUrl)
		.query<AvailabilityByLocationQuery>(AvailabilityByLocationDocument, {
			brand: BffBrandType.Lovet,
			facilityId,
			appointmentTypes,
			dateFrom,
			dateTo,
			intelligentSchedulingEnabled,
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			intelligentSchedulingPayload: intelligentSchedulingPayload as any,
		})
		.toPromise();

	const slots = result.data?.availabilityByLocation?.availableSlots ?? [];

	// Two-pass overflow filter (DCP-13324):
	// Pass 1 — collect all providerIds whose username is 'overflow' (case-insensitive).
	//           Some overflow slots lack a provider.username on earlier entries but share the
	//           same providerId as a later slot that does carry the username, so we must check
	//           the whole list before deciding which providerIds to drop.
	const overflowProviderIds = new Set(
		slots
			.filter((slot) => slot.provider?.username?.toLowerCase() === 'overflow')
			.map((slot) => slot.providerId)
			.filter(Boolean)
	);

	// Pass 2 — remove every slot belonging to an identified overflow provider.
	return slots.filter((slot) => !overflowProviderIds.has(slot.providerId ?? ''));
}
